import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { ChevronRight, Search, X } from "lucide-react";
import Sidebar from "../../customComponents/Sidebar/Sidebar";
import {
  getCounts,
  getEntries,
  processEntries,
  sendCredentials,
  sendCredentialsBulk,
  getRegistration,
  checkinByCode,
  getActivity,
} from "./apiRoutes/entries";
import { MetricStat } from "../EventMetrics/pageComponents/ChartComponents";

/**
 * "2026-09-30 23:02:48" reads as noise in a list. Show the clock time, and the
 * date only when it is not today.
 */
const formatActivityTime = (value) => {
  if (!value) {
    return "";
  }

  const stamp = new Date(value.replace(" ", "T"));

  if (Number.isNaN(stamp.getTime())) {
    return value;
  }

  const time = stamp.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });

  if (stamp.toDateString() === new Date().toDateString()) {
    return time;
  }

  return `${stamp.toLocaleDateString([], { month: "short", day: "numeric" })} ${time}`;
};

/**
 * Below this width the pages use the compact layout: cards instead of the
 * table, stacked toolbars, the sidebar as a hamburger. Kept in sync by hand
 * with the @media rules in the page stylesheets and sidebar.css.
 */
const COMPACT_BREAKPOINT = 1024;

/** Kept small so a single request never approaches the PHP time limit. */
const BATCH_SIZE = 10;

const chunk = (items, size) => {
  const out = [];

  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }

  return out;
};

function EventEntries() {
  /*
   * Tablets get the same UI as phones, so the switch is at 1024px - wide
   * enough to cover every iPad in portrait and an iPad in landscape. It
   * deliberately matches the stylesheets and the Sidebar hamburger rather
   * than any MUI breakpoint; change COMPACT_BREAKPOINT and the @media rules
   * in the four page stylesheets together or they will disagree.
   */
  const isCompact = useMediaQuery(`(max-width:${COMPACT_BREAKPOINT}px)`);

  const isAdmin = !!tmtEntries.is_admin;
  const isVolunteer = !!tmtEntries.is_staff;

  /* Volunteers may look and re-send; only admins may create registrations. */
  const canView = isAdmin || isVolunteer;
  const canBulkSend = isAdmin;

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState([]);

  /* Row awaiting confirmation for a credentials reset. */
  const [credentialsRow, setCredentialsRow] = useState(null);

  /*
   * A second scrollbar above the table. Twelve columns overflow on anything
   * short of a wide monitor, and the only scrollbar was at the very bottom -
   * past every row - so the columns on the right were easy to miss entirely.
   */
  const tableRef = useRef(null);
  const topScrollRef = useRef(null);
  const [scrollWidth, setScrollWidth] = useState(0);

  const [activity, setActivity] = useState([]);
  const [activityOpen, setActivityOpen] = useState(false);

  /* Row awaiting confirmation for a single QR send. */
  const [qrRow, setQrRow] = useState(null);

  /*
   * Manual check-in runs in three steps: look the registration up, show who
   * it is, then write it. Nothing is recorded until the confirm button.
   */
  const [checkinRow, setCheckinRow] = useState(null);
  const [checkinDetails, setCheckinDetails] = useState(null);
  const [checkinDone, setCheckinDone] = useState(null);
  const [checkinBusy, setCheckinBusy] = useState(false);
  const [checkinError, setCheckinError] = useState("");
  const [bulkCredentialsOpen, setBulkCredentialsOpen] = useState(false);

  useEffect(() => {
    if (!canView) {
      window.location.href = "portal-login";
      return;
    }

    load();
  }, [filter, search]);

  // Wait for a pause in typing rather than firing a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 350);

    return () => clearTimeout(timer);
  }, [searchInput]);

  /*
   * Kept separate from load() so a failure here never blocks the entries
   * list; the log is supporting information, not the page's job.
   */
  const loadActivity = async () => {
    try {
      const response = await getActivity(50);
      setActivity(response.rows ?? []);
    } catch (e) {
      setActivity([]);
    }
  };

  /* Keep the dummy bar's width in step with the real table's. */
  useEffect(() => {
    const table = tableRef.current;

    if (!table) {
      setScrollWidth(0);

      return undefined;
    }

    const measure = () => setScrollWidth(table.scrollWidth);

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(table);

    return () => observer.disconnect();
  }, [rows, loading, isCompact, canBulkSend]);

  /*
   * Mirror the two scroll positions. Assigning scrollLeft fires another scroll
   * event, but the values have already converged by then so it settles rather
   * than looping.
   */
  const syncScroll = (from, to) => () => {
    if (from.current && to.current && to.current.scrollLeft !== from.current.scrollLeft) {
      to.current.scrollLeft = from.current.scrollLeft;
    }
  };

  const load = async () => {
    setLoading(true);
    setError("");

    try {
      const [list, totals] = await Promise.all([
        getEntries(1, 200, filter, search),
        getCounts(),
      ]);
      setRows(list.rows ?? []);
      setCounts(totals);
      setSelected([]);
      loadActivity();
    } catch (e) {
      setError("Could not load entries. You may not have permission, or WPForms is unavailable.");
    } finally {
      setLoading(false);
    }
  };

  /*
   * Selection just means "rows I am interested in". Each bulk action decides
   * what it can actually do, and the server skips the rest: QR sending skips
   * anyone already emailed, credentials skip anyone who already has them.
   */
  const selectableIds = useMemo(
    () => rows.filter((r) => r.valid_email).map((r) => r.entry_id),
    [rows]
  );

  /** Rows a bulk credentials run would actually act on. */
  const credentialsTargets = useMemo(
    () =>
      rows.filter(
        (r) =>
          selected.includes(r.entry_id) &&
          r.valid_email &&
          r.has_registration &&
          !r.credentials_at
      ),
    [rows, selected]
  );

  const allSelected = selectableIds.length > 0 && selected.length === selectableIds.length;

  const toggleAll = () => setSelected(allSelected ? [] : selectableIds);

  const toggleOne = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));

  const run = async (entryIds, resend = false) => {
    setConfirmOpen(false);
    setRunning(true);
    setResults([]);
    setProgress({ done: 0, total: entryIds.length });

    const collected = [];

    for (const batch of chunk(entryIds, BATCH_SIZE)) {
      try {
        const response = await processEntries(batch, resend);
        collected.push(...(response.results ?? []));
      } catch (e) {
        batch.forEach((id) =>
          collected.push({ entry_id: id, status: "error", message: "Request failed." })
        );
      }

      setProgress((p) => ({ ...p, done: p.done + batch.length }));
      setResults([...collected]);
    }

    setRunning(false);
    await load();
  };

  const runBulkCredentials = async () => {
    setBulkCredentialsOpen(false);

    const ids = credentialsTargets.map((r) => r.entry_id);

    setRunning(true);
    setResults([]);
    setProgress({ done: 0, total: ids.length });

    const collected = [];

    for (const batch of chunk(ids, BATCH_SIZE)) {
      try {
        const response = await sendCredentialsBulk(batch);
        collected.push(...(response.results ?? []));
      } catch (e) {
        batch.forEach((id) =>
          collected.push({ entry_id: id, status: "error", message: "Request failed." })
        );
      }

      setProgress((p) => ({ ...p, done: p.done + batch.length }));
      setResults([...collected]);
    }

    setRunning(false);
    await load();
  };

  const runCredentials = async (row) => {
    setCredentialsRow(null);
    setRunning(true);
    setResults([]);
    setProgress({ done: 0, total: 1 });

    try {
      const response = await sendCredentials(row.entry_id);
      setResults(response.results ?? []);
    } catch (e) {
      setResults([
        { entry_id: row.entry_id, status: "error", message: "Request failed." },
      ]);
    }

    setProgress({ done: 1, total: 1 });
    setRunning(false);
    await load();
  };

  const openCheckin = async (row) => {
    setCheckinRow(row);
    setCheckinDetails(null);
    setCheckinDone(null);
    setCheckinError("");
    setCheckinBusy(true);

    try {
      setCheckinDetails(await getRegistration(row.code));
    } catch (e) {
      setCheckinError("Could not read this registration. Its attendance code may be missing.");
    } finally {
      setCheckinBusy(false);
    }
  };

  const confirmCheckin = async () => {
    setCheckinBusy(true);
    setCheckinError("");

    try {
      const response = await checkinByCode(checkinRow.code, checkinRow.entry_id);

      if (response.status === "error") {
        setCheckinError(response.message || "Check-in failed.");
      } else {
        setCheckinDone(response.registration ?? checkinDetails);
        await load();
      }
    } catch (e) {
      setCheckinError("Check-in failed.");
    } finally {
      setCheckinBusy(false);
    }
  };

  const closeCheckin = () => {
    setCheckinRow(null);
    setCheckinDetails(null);
    setCheckinDone(null);
    setCheckinError("");
  };

  const summary = useMemo(() => {
    const by = { sent: 0, registered: 0, skipped: 0, error: 0 };
    results.forEach((r) => {
      by[r.status] = (by[r.status] || 0) + 1;
    });
    return by;
  }, [results]);

  if (!canView) {
    return null;
  }

  const sendingBlocked = !tmtEntries.email_enabled;
  const redirecting = tmtEntries.email_enabled && !tmtEntries.is_production;

  return (
    <div className="admin-portal-container">
      <Sidebar selectedIndex={3} />

      <Stack direction="column" sx={{ pt: 2, width: "100%", px: { sm: 2, lg: 4 } }} gap={3}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Registration Entries
        </Typography>

        {sendingBlocked && (
          <div className="entries-banner entries-banner--warn">
            <strong>Email sending is switched off.</strong> Running this will create registrations
            and QR codes, but nobody will be emailed. Set <code>TMT_INS_EVENT_EMAIL_ENABLED</code> to
            enable sending.
          </div>
        )}

        {redirecting && (
          <div className="entries-banner entries-banner--info">
            <strong>Non-production environment.</strong> Every email is redirected to{" "}
            <strong>{tmtEntries.redirect_to || "an unconfigured address"}</strong> instead of the
            real registrant.
          </div>
        )}

        {counts && (
          <div className="entries-stats">
            <Box className="entries-stat" sx={{ height: 140, flex: 1 }}>
              <MetricStat title="Total Entries" value={counts.total} />
            </Box>
            <Box className="entries-stat" sx={{ height: 140, flex: 1 }}>
              <MetricStat title="Registered" value={counts.registered} />
            </Box>
            <Box className="entries-stat" sx={{ height: 140, flex: 1 }}>
              <MetricStat title="QR Emailed" value={counts.emailed} />
            </Box>
            <Box className="entries-stat" sx={{ height: 140, flex: 1 }}>
              <MetricStat title="Pending" value={counts.pending} />
            </Box>
          </div>
        )}

        <Stack
          className="entries-toolbar"
          direction="row"
          gap={2}
          alignItems="center"
          flexWrap="wrap"
        >
          <ToggleButtonGroup
            size="small"
            exclusive
            value={filter}
            onChange={(e, v) => v && setFilter(v)}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="pending">Pending</ToggleButton>
            <ToggleButton value="sent">Already emailed</ToggleButton>
          </ToggleButtonGroup>

          {canBulkSend && (
            <Button
              variant="contained"
              disabled={selected.length === 0 || running}
              onClick={() => setConfirmOpen(true)}
            >
              Generate &amp; Send ({selected.length})
            </Button>
          )}

          {canBulkSend && (
            <Button
              variant="outlined"
              color="warning"
              disabled={credentialsTargets.length === 0 || running}
              onClick={() => setBulkCredentialsOpen(true)}
            >
              Send Credentials ({credentialsTargets.length})
            </Button>
          )}

          <TextField
            size="small"
            placeholder="Search name or email"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={() => setSearchInput("")}
                  >
                    <X size={14} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          <Button variant="outlined" onClick={load} disabled={running || loading}>
            Refresh
          </Button>
        </Stack>

        {running && (
          <Box>
            <LinearProgress
              variant="determinate"
              value={progress.total ? (progress.done / progress.total) * 100 : 0}
            />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Processing {progress.done} of {progress.total}…
            </Typography>
          </Box>
        )}

        {results.length > 0 && !running && (
          <div className="entries-banner entries-banner--info">
            Finished — {summary.sent || 0} emailed, {summary.registered || 0} registered without
            email, {summary.skipped || 0} skipped, {summary.error || 0} failed.
            {summary.error > 0 && (
              <ul className="entries-errors">
                {results
                  .filter((r) => r.status === "error")
                  .map((r) => (
                    <li key={r.entry_id}>
                      Entry {r.entry_id}: {r.message}
                    </li>
                  ))}
              </ul>
            )}
          </div>
        )}

        {error && <div className="entries-banner entries-banner--error">{error}</div>}

        {/*
          * Who was admitted, by whom and how. Collapsed by default: at the
          * door the list of entries is what matters, and this is for checking
          * afterwards.
          */}
        <div className="entries-activity">
          <button
            type="button"
            className="entries-activity__toggle"
            aria-expanded={activityOpen}
            onClick={() => setActivityOpen((open) => !open)}
          >
            <span className="entries-activity__heading">
              <ChevronRight
                size={16}
                className="entries-activity__chevron"
                aria-hidden="true"
              />
              Activity log
            </span>
            <span className="entries-activity__count">
              {activity.length === 0
                ? "No check-ins yet"
                : `${activity.length} recent`}
            </span>
          </button>

          {activityOpen && activity.length > 0 && (
            <ul className="entries-activity__list">
              {activity.map((item) => (
                <li className="entries-activity__item" key={item.id}>
                  <time className="entries-activity__time">
                    {formatActivityTime(item.created_at)}
                  </time>

                  <div className="entries-activity__body">
                    <div className="entries-activity__line">
                      <span className="entries-activity__subject">
                        {item.subject_name || "Unknown"}
                      </span>
                      <span
                        className={`entries-activity__tag entries-activity__tag--${item.source}`}
                      >
                        {item.source === "manual" ? "Manual" : "Scanned"}
                      </span>
                    </div>

                    <div className="entries-activity__meta">
                      <span>{item.actor_name}</span>
                      {item.note && <span>{item.note}</span>}
                      {item.code && (
                        <span className="entries-activity__code">{item.code}</span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {activityOpen && activity.length === 0 && (
            <Typography sx={{ py: 2, color: "#666" }}>
              Nothing recorded yet. Check-ins made from this page and at the
              door both appear here.
            </Typography>
          )}
        </div>

        {loading ? (
          <CircularProgress />
        ) : isCompact ? (
          /*
           * Cards below 1024px, on tablets as well as phones. The nine-column
           * table rendered 1117px wide on a phone, putting Status, Credentials
           * and Action off-screen entirely.
           * Same rows and handlers as the table below - the markup is what is
           * duplicated, so a column change has to be made in both places.
           */
          <div className="entries-cards">
            {canBulkSend && rows.length > 0 && (
              /* The table's select-all lives in the header row, which is gone here. */
              <label className="entries-cards__selectall">
                <Checkbox
                  checked={allSelected}
                  indeterminate={selected.length > 0 && !allSelected}
                  onChange={toggleAll}
                  disabled={selectableIds.length === 0}
                />
                <span>Select all ({selectableIds.length})</span>
              </label>
            )}

            {rows.length === 0 && (
              <Typography sx={{ py: 3, textAlign: "center", color: "#666" }}>
                {search ? `No entries match "${search}".` : "No entries match this filter."}
              </Typography>
            )}

            {rows.map((row) => (
              <div className="entry-card" key={row.entry_id}>
                <div className="entry-card__head">
                  {canBulkSend && (
                    <Checkbox
                      checked={selected.includes(row.entry_id)}
                      onChange={() => toggleOne(row.entry_id)}
                      disabled={!row.valid_email}
                      inputProps={{ "aria-label": `Select entry ${row.entry_id}` }}
                    />
                  )}
                  <div className="entry-card__identity">
                    <Typography className="entry-card__name">{row.name}</Typography>
                    <Typography className="entry-card__email">
                      {row.email}
                      {!row.valid_email && (
                        <Chip size="small" color="error" label="invalid" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                    {row.phone && (
                      <Typography className="entry-card__phone">{row.phone}</Typography>
                    )}
                  </div>
                </div>

                <div className="entry-card__meta">
                  <span>#{row.entry_id}</span>
                  <span>{row.date}</span>
                  <span>
                    {row.adult_count || 0}A / {row.child_count || 0}C
                  </span>
                </div>

                <div className="entry-card__chips">
                  {row.emailed_at ? (
                    <Chip size="small" color="success" label="Emailed" />
                  ) : row.has_registration ? (
                    <Chip size="small" color="warning" label="Registered, not emailed" />
                  ) : (
                    <Chip size="small" label="No registration" />
                  )}
                  {canBulkSend &&
                    (row.credentials_at ? (
                      <Chip size="small" color="success" label="Credentials sent" />
                    ) : (
                      <Chip size="small" label="No credentials" />
                    ))}
                  {row.checked_in_at && (
                    <Chip size="small" color="primary" label="Checked in" />
                  )}
                  {/* Both states, matching the Type column on the table. */}
                  {row.is_walk_in ? (
                    <Chip size="small" color="secondary" label="Walk-in" />
                  ) : (
                    <Chip size="small" variant="outlined" label="Pre-reg" />
                  )}
                  {/* No column header here, so each chip states its question. */}
                  {row.is_first_time === true && (
                    <Chip size="small" color="success" label="First time: Yes" />
                  )}
                  {row.is_first_time === false && (
                    <Chip size="small" color="error" label="First time: No" />
                  )}
                  {row.is_first_time === null && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label="First time: Undefined"
                      className="chip-undefined"
                    />
                  )}
                  {row.is_tmt_customer === true && (
                    <Chip size="small" color="success" label="TMT customer: Yes" />
                  )}
                  {row.is_tmt_customer === false && (
                    <Chip size="small" color="error" label="TMT customer: No" />
                  )}
                  {row.is_tmt_customer === null && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label="TMT customer: Undefined"
                      className="chip-undefined"
                    />
                  )}
                  {row.code && <span className="entries-code">{row.code}</span>}
                </div>

                {/*
                  * Check-in is the primary action at the door, so it gets the
                  * full width; the two send actions share a row beneath rather
                  * than stacking, which cost half the card's height.
                  */}
                <div className="entry-card__actions">
                  {/*
                    * Someone already checked in is the exception, so that
                    * button is played down - a solid primary button on every
                    * card makes the ones still to arrive harder to pick out.
                    */}
                  <Button
                    variant={row.checked_in_at ? "outlined" : "contained"}
                    color={row.checked_in_at ? "inherit" : "primary"}
                    fullWidth
                    disabled={running || !row.code}
                    onClick={() => openCheckin(row)}
                  >
                    {row.checked_in_at ? "Check in again" : "Check in"}
                  </Button>

                  <div className="entry-card__actions-row">
                    {canBulkSend && (
                      <Button
                        variant="outlined"
                        color="warning"
                        disabled={running || !row.has_registration}
                        onClick={() => setCredentialsRow(row)}
                      >
                        Credentials
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      disabled={
                        running || !row.valid_email || (!canBulkSend && !row.has_registration)
                      }
                      onClick={() => setQrRow(row)}
                    >
                      {row.emailed_at ? "Resend QR" : "Send QR"}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            <div
              className="entries-scroll-top"
              ref={topScrollRef}
              onScroll={syncScroll(topScrollRef, tableRef)}
              aria-hidden="true"
            >
              <div style={{ width: scrollWidth }} />
            </div>

            <TableContainer
              className="entries-table"
              ref={tableRef}
              onScroll={syncScroll(tableRef, topScrollRef)}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {canBulkSend && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allSelected}
                          indeterminate={selected.length > 0 && !allSelected}
                          onChange={toggleAll}
                          disabled={selectableIds.length === 0}
                        />
                      </TableCell>
                    )}
                    <TableCell>Name</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell align="center">Party</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>First time</TableCell>
                    <TableCell>TMT customer</TableCell>
                    <TableCell>Status</TableCell>
                    {canBulkSend && <TableCell>Credentials</TableCell>}
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={canBulkSend ? 10 : 8}>
                        <Typography sx={{ py: 3, textAlign: "center", color: "#666" }}>
                          {search
                            ? `No entries match "${search}".`
                            : "No entries match this filter."}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}

                  {rows.map((row) => (
                    <TableRow key={row.entry_id} hover>
                      {canBulkSend && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selected.includes(row.entry_id)}
                            onChange={() => toggleOne(row.entry_id)}
                            /*
                             * Selectable whenever there is an address to mail.
                             * Each bulk action filters the selection itself, so
                             * a row that is done for one action can still be
                             * picked for the other.
                             */
                            disabled={!row.valid_email}
                          />
                        </TableCell>
                      )}
                      <TableCell>{row.name}</TableCell>
                      <TableCell>
                        {/*
                          * Email clipped with the full address on hover: these
                          * run long enough to dominate the table, and the local
                          * part is what identifies someone anyway. The phone
                          * shares the cell rather than taking a column of its
                          * own, both being contact details.
                          */}
                        <span className="entries-email" title={row.email}>
                          {row.email}
                        </span>
                        {!row.valid_email && (
                          <Chip size="small" color="error" label="invalid" sx={{ ml: 1 }} />
                        )}
                        {row.phone && (
                          <span className="entries-phone">{row.phone}</span>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {row.adult_count || 0}A / {row.child_count || 0}C
                      </TableCell>
                      <TableCell>
                        {row.is_walk_in ? (
                          <Chip size="small" color="secondary" label="Walk-in" />
                        ) : (
                          <Chip size="small" variant="outlined" label="Pre-reg" />
                        )}
                      </TableCell>
                      <TableCell>
                        {row.is_first_time === true && (
                          <Chip size="small" color="success" label="Yes" />
                        )}
                        {row.is_first_time === false && (
                          <Chip size="small" color="error" label="No" />
                        )}
                        {row.is_first_time === null && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label="Undefined"
                            className="chip-undefined"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {row.is_tmt_customer === true && (
                          <Chip size="small" color="success" label="Yes" />
                        )}
                        {row.is_tmt_customer === false && (
                          <Chip size="small" color="error" label="No" />
                        )}
                        {row.is_tmt_customer === null && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label="Undefined"
                            className="chip-undefined"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {row.emailed_at ? (
                          <Chip size="small" color="success" label="Emailed" />
                        ) : row.has_registration ? (
                          <Chip size="small" color="warning" label="Registered, not emailed" />
                        ) : (
                          <Chip size="small" label="No registration" />
                        )}
                        {row.checked_in_at && (
                          <Chip
                            size="small"
                            color="primary"
                            label="Checked in"
                            sx={{ ml: 0.5 }}
                          />
                        )}
                        {row.code && (
                          <span className="entries-code">{row.code}</span>
                        )}
                      </TableCell>
                      {canBulkSend && (
                        <TableCell>
                          {row.credentials_at ? (
                            <Chip size="small" color="success" label="Sent" />
                          ) : (
                            <Chip size="small" label="Not sent" />
                          )}
                        </TableCell>
                      )}
                      <TableCell align="right">
                        <Button
                          size="small"
                          color={row.checked_in_at ? "inherit" : "primary"}
                          disabled={running || !row.code}
                          title={
                            row.code
                              ? "Record attendance for this registrant"
                              : "No attendance code - register this entry first"
                          }
                          onClick={() => openCheckin(row)}
                        >
                          {row.checked_in_at ? "Check in again" : "Check in"}
                        </Button>
                        {canBulkSend && (
                          <Button
                            size="small"
                            color="warning"
                            disabled={running || !row.has_registration}
                            title={
                              row.has_registration
                                ? "Issues a new password and emails it"
                                : "No account yet - register this entry first"
                            }
                            onClick={() => setCredentialsRow(row)}
                          >
                            Credentials
                          </Button>
                        )}
                        <Button
                          size="small"
                          disabled={
                            running ||
                            !row.valid_email ||
                            (!canBulkSend && !row.has_registration)
                          }
                          title={
                            !canBulkSend && !row.has_registration
                              ? "An administrator must register this entry first"
                              : undefined
                          }
                          onClick={() => setQrRow(row)}
                        >
                          {row.emailed_at ? "Resend" : "Send"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

      </Stack>

      {/*
        * A single QR send goes out to a real person, so it is confirmed the
        * same way the bulk run is.
        */}
      <Dialog open={!!qrRow} onClose={() => setQrRow(null)} fullWidth maxWidth="xs">
        <DialogTitle>
          {qrRow?.emailed_at ? "Resend the QR code?" : "Send the QR code?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {qrRow?.emailed_at && (
              <div className="entries-banner entries-banner--warn">
                Already emailed on {qrRow.emailed_at}. They will receive it a
                second time.
              </div>
            )}

            <dl className="checkin-details">
              <dt>Name</dt>
              <dd>{qrRow?.name || "—"}</dd>

              <dt>Email</dt>
              <dd>{qrRow?.email}</dd>

              <dt>Party</dt>
              <dd>
                {qrRow?.adult_count || 0}A / {qrRow?.child_count || 0}C
              </dd>
            </dl>

            {sendingBlocked && (
              <div className="entries-banner entries-banner--warn">
                Email sending is switched off. The registration and QR code will
                be created, but nothing will be delivered.
              </div>
            )}

            {redirecting && (
              <div className="entries-banner entries-banner--info">
                This environment redirects mail, so it goes to{" "}
                <strong>{tmtEntries.redirect_to || "an unconfigured address"}</strong>,
                not to {qrRow?.email}.
              </div>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrRow(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              const target = qrRow;
              setQrRow(null);
              run([target.entry_id], true);
            }}
          >
            {qrRow?.emailed_at ? "Resend" : "Send"}
          </Button>
        </DialogActions>
      </Dialog>

      {/*
        * Manual check-in. Step one shows who the code belongs to and how many
        * people it admits; nothing is written until Confirm. Step two reports
        * what was recorded.
        */}
      <Dialog open={!!checkinRow} onClose={closeCheckin} fullWidth maxWidth="xs">
        <DialogTitle>
          {checkinDone ? "Checked in" : "Confirm check-in"}
        </DialogTitle>
        <DialogContent>
          {checkinBusy && !checkinDetails && !checkinDone && <CircularProgress />}

          {checkinError && (
            <div className="entries-banner entries-banner--error">{checkinError}</div>
          )}

          {(checkinDone || checkinDetails) && (
            <>
              {checkinDone && (
                <div className="entries-banner entries-banner--info">
                  Attendance recorded for <strong>{checkinDone.name}</strong>.
                </div>
              )}

              {!checkinDone && checkinDetails.was_checked_in && (
                <div className="entries-banner entries-banner--warn">
                  Already checked in
                  {checkinDetails.checkin_at ? ` at ${checkinDetails.checkin_at}` : ""}.
                  Confirming again updates the time.
                </div>
              )}

              <dl className="checkin-details">
                <dt>Name</dt>
                <dd>{(checkinDone ?? checkinDetails).name || "—"}</dd>

                <dt>Phone</dt>
                <dd className="checkin-details__phone">
                  {(checkinDone ?? checkinDetails).phone || (
                    <span className="checkin-details__muted">Not given</span>
                  )}
                </dd>

                <dt>Event</dt>
                <dd>{(checkinDone ?? checkinDetails).event_title || "—"}</dd>

                <dt>Party</dt>
                <dd>
                  {(checkinDone ?? checkinDetails).adult_count ?? 0} adult
                  {1 === (checkinDone ?? checkinDetails).adult_count ? "" : "s"},{" "}
                  {(checkinDone ?? checkinDetails).child_count ?? 0}{" "}
                  {1 === (checkinDone ?? checkinDetails).child_count
                    ? "child"
                    : "children"}
                </dd>

                <dt>Code</dt>
                <dd className="checkin-details__code">
                  {(checkinDone ?? checkinDetails).code}
                </dd>

                <dt>Type</dt>
                <dd>
                  {(checkinDone ?? checkinDetails).is_walk_in ? (
                    <Chip size="small" color="secondary" label="Walk-in" />
                  ) : (
                    <Chip size="small" variant="outlined" label="Pre-registered" />
                  )}
                </dd>

                <dt>First time</dt>
                <dd>
                  {(checkinDone ?? checkinDetails).is_first_time === true && (
                    <Chip size="small" color="success" label="Yes" />
                  )}
                  {(checkinDone ?? checkinDetails).is_first_time === false && (
                    <Chip size="small" color="error" label="No" />
                  )}
                  {null === ((checkinDone ?? checkinDetails).is_first_time ?? null) && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label="Undefined"
                      className="chip-undefined"
                    />
                  )}
                </dd>

                <dt>TMT customer</dt>
                <dd>
                  {(checkinDone ?? checkinDetails).is_tmt_customer === true && (
                    <Chip size="small" color="success" label="Yes" />
                  )}
                  {(checkinDone ?? checkinDetails).is_tmt_customer === false && (
                    <Chip size="small" color="error" label="No" />
                  )}
                  {null === ((checkinDone ?? checkinDetails).is_tmt_customer ?? null) && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label="Undefined"
                      className="chip-undefined"
                    />
                  )}
                </dd>
              </dl>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCheckin}>{checkinDone ? "Close" : "Cancel"}</Button>
          {!checkinDone && (
            <Button
              variant="contained"
              disabled={checkinBusy || !checkinDetails}
              onClick={confirmCheckin}
            >
              Confirm check-in
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkCredentialsOpen}
        onClose={() => setBulkCredentialsOpen(false)}
      >
        <DialogTitle>
          Send credentials to {credentialsTargets.length}{" "}
          {credentialsTargets.length === 1 ? "person" : "people"}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Each of them gets a <strong>brand new password</strong> emailed to
            them. Their current password stops working and they are signed out
            everywhere.
            <br />
            <br />
            Anyone who already received credentials is left untouched, as are
            registrants with no email address. Of the {selected.length} rows you
            selected, <strong>{credentialsTargets.length}</strong> will be sent.
            {sendingBlocked && (
              <>
                <br />
                <br />
                <strong>
                  Email sending is switched off, so passwords would be reset
                  with nothing delivered.
                </strong>
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkCredentialsOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={sendingBlocked}
            onClick={runBulkCredentials}
          >
            Reset &amp; Send
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!credentialsRow} onClose={() => setCredentialsRow(null)}>
        <DialogTitle>Send new credentials to {credentialsRow?.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This issues a <strong>brand new password</strong> for{" "}
            {credentialsRow?.email} and emails it to them.
            <br />
            <br />
            Their current password will stop working immediately, and they will
            be signed out everywhere. Only do this if they cannot sign in.
            {sendingBlocked && (
              <>
                <br />
                <br />
                <strong>
                  Email sending is switched off, so the password will be reset
                  but nothing will be delivered.
                </strong>
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCredentialsRow(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => runCredentials(credentialsRow)}
          >
            Reset &amp; Send
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Send QR codes to {selected.length} people?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {sendingBlocked
              ? "Email sending is currently switched off, so registrations and QR codes will be created but no email will go out."
              : redirecting
              ? `This environment redirects mail, so all ${selected.length} messages go to ${
                  tmtEntries.redirect_to || "an unconfigured address"
                } — not to the registrants.`
              : `This will email ${selected.length} real registrants. Anyone already emailed has been excluded.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => run(selected)}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default EventEntries;
